import { ReactElement, useState } from "react";
import {
  Datagrid,
  FunctionField,
  NumberField,
  TextField,
  useListContext,
  useRefresh,
  useRecordContext,
  useUpdate,
} from "react-admin";
import { StudentBranchField } from "../fields/StudentBranchField";
import { DrivingSchool } from "../providers/drivingSchoolsProvider";
import { MoneyField } from "../fields/MoneyField";
import { StudentsListSkeleton } from "./StudentsListSkeleton";
import { DateField } from "../fields/DateField";
import { Avatar, Box, Chip, Tooltip, useMediaQuery } from "@mui/material";
import type { SxProps, Theme } from "@mui/material";
import { Student, getStudentStatusDisplay, studentStatusColors } from "../providers/studentsProvider";
import { dotStyle } from "../misc/backofficeTheme";
import TimelineDot from "@mui/lab/TimelineDot";
import AddRoadIcon from "@mui/icons-material/AddRoad";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PaymentIcon from "@mui/icons-material/Payment";
import { TheorieunterrichtIcon } from "../icons/TheorieunterrichtIcon";
import { CarIcon } from "../icons/CarIcon";
import { ManualGearIcon } from "../icons/ManualGearIcon";
import { SimulatorIcon } from "../icons/SimulatorIcon";
import { StudentLabels } from "../misc/StudentLabels";
import { Column } from "../misc/Column";
import { DatagridHeader } from "../misc/DatagridHeader";
import { ContextMenu } from "../misc/ContextMenu";
import { grants } from "../backoffice.access_control";
import { useNavigate } from "react-router";
import { StudentMobileListItem } from "./StudentMobileListItem";
import { assertNever } from "../backoffice.utils";
import { isForCar } from "../model/DrivingLicenseClass";

const compactChipSx: SxProps<Theme> = {
  color: "text.secondary",
  fontSize: "0.75rem",
  height: 20,
  "& .MuiChip-label": { px: 0.75 },
};

const paymentChipSx: SxProps<Theme> = {
  ...compactChipSx,
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
};

export function StudentsDataGrid({
  drivingSchool,
  showSimulatorDrivingLessons,
  empty,
  sx,
}: {
  drivingSchool: DrivingSchool;
  showSimulatorDrivingLessons: boolean;
  empty?: ReactElement;
  sx?: SxProps;
}) {
  const { isLoading, data, total, perPage, error } = useListContext<Student>();
  const refresh = useRefresh();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const [update, { isLoading: isUpdatingStudent }] = useUpdate();
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; studentId: string } | null>(null);

  if (isLoading && !data?.length && !error) {
    return (
      <StudentsListSkeleton
        rowCount={perPage}
        showBranch={drivingSchool.branches.length > 1}
        showSimulatorDrivingLessons={showSimulatorDrivingLessons}
      />
    );
  }

  const handleContextMenu = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const tr = (event.target as HTMLElement).closest("tbody tr");
    if (!tr || !(tr instanceof HTMLTableRowElement)) return;
    const student = data?.[tr.sectionRowIndex];
    if (!student) return;
    setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, studentId: String(student.id) });
  };

  const handleClose = () => setContextMenu(null);
  const selectedStudent = data?.find((student) => String(student.id) === contextMenu?.studentId);

  const handleMobileItemClick = (event: React.MouseEvent<HTMLDivElement>, studentId: string) => {
    if (event.ctrlKey || event.metaKey) {
      window.open(`/students/${studentId}`, "_blank");
      return;
    }
    navigate(`/students/${studentId}`);
  };

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((student) => (
          <StudentMobileListItem
            key={student.id}
            drivingSchool={drivingSchool}
            showSimulatorDrivingLessons={showSimulatorDrivingLessons}
            student={student}
            onClick={(event) => handleMobileItemClick(event, String(student.id))}
          />
        ))}
      </Column>
    );
  }

  const handleOpenInNewTab = () => {
    window.open(`/students/${contextMenu?.studentId}`, "_blank");
    handleClose();
  };

  const handleSetArchived = (archived: boolean) => {
    if (!selectedStudent) {
      return;
    }

    void runSafely(
      () =>
        update(
          "students",
          {
            id: selectedStudent.id,
            data: { archived },
            previousData: selectedStudent,
          },
          { returnPromise: true },
        ),
      {
        successMessage: archived
          ? "Fahrschüler wurde erfolgreich archiviert."
          : "Archivstatus des Fahrschülers wurde erfolgreich aufgehoben.",
        errorMessage: archived
          ? "Fahrschüler konnte nicht archiviert werden."
          : "Archivstatus des Fahrschülers konnte nicht aufgehoben werden.",
        onSuccess: () => {
          refresh();
          handleClose();
        },
      },
    );
  };

  return (
    <div onContextMenu={handleContextMenu}>
      <Datagrid
        header={<DatagridHeader />}
        isLoading={false}
        empty={empty}
        rowClick={(id, _resource, _record) => {
          if ((window.event as MouseEvent | undefined)?.ctrlKey || (window.event as MouseEvent | undefined)?.metaKey) {
            window.open(`/students/${id}`, "_blank");
            return false;
          }
          return "edit";
        }}
        bulkActionButtons={false}
        className="students-list"
        sx={sx}
      >
        <FunctionField label="Name" source="name" render={renderAvatarAndName} />
        {drivingSchool.branches.length > 1 && <StudentBranchField label="Filiale" />}
        <FunctionField label="Ausbildung" source="bookedTrainingsSummary" render={renderBookedTrainings} />
        <DateField label="Anmeldung" source="startDate" />
        <_KpiNumberField
          label={<_IconLabel icon={<TheorieunterrichtIcon />} title="Theorieunterricht" />}
          source="kpis.finishedTheoryLessonsCount"
          getValue={(student) => student.kpis.finishedTheoryLessonsCount}
          textAlign="right"
        />
        {showSimulatorDrivingLessons && (
          <_KpiNumberField
            label={<_IconLabel icon={<SimulatorIcon />} title="Simulatorstunden" />}
            source="kpis.finishedSimulatorDrivingLessonsCount"
            getValue={(student) => student.kpis.finishedSimulatorDrivingLessonsCount}
            options={{ maximumFractionDigits: 2 }}
            textAlign="right"
          />
        )}
        <_KpiNumberField
          label={<_IconLabel icon={<CarIcon />} title="Fahrstunden" />}
          source="kpis.finishedNormalDrivingLessonsCount"
          getValue={(student) => student.kpis.finishedNormalDrivingLessonsCount}
          options={{ maximumFractionDigits: 2 }}
          textAlign="right"
        />
        <_KpiNumberField
          label={<_IconLabel icon={<AddRoadIcon />} title="Sonderfahrten" />}
          source="kpis.finishedSpecialDrivingLessonsCount"
          getValue={(student) => student.kpis.finishedSpecialDrivingLessonsCount}
          options={{ maximumFractionDigits: 2 }}
          textAlign="right"
        />
        <_KpiNumberField
          label={<_IconLabel icon={<ManualGearIcon />} title="Schaltstunden" />}
          source={"kpis.finishedManualGearLessonsCount"}
          getValue={(student) => student.kpis.finishedManualGearLessonsCount}
          options={{ maximumFractionDigits: 2 }}
          textAlign="right"
        />
        <_StatusField label="Status" source="verboseStatus" />
        <MoneyField label="Saldo" source="balance" redForNegativeValues />
        <FunctionField
          label={<_IconLabel icon={<PaymentIcon />} title="Zahlungsart" />}
          source="paymentStrategy"
          render={renderPaymentStrategy}
        />
      </Datagrid>
      <ContextMenu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorPosition={contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
      >
        <ContextMenu.Item onClick={handleOpenInNewTab}>In neuem Tab öffnen</ContextMenu.Item>
        {grants.has("editStudentPersonalData") && selectedStudent && selectedStudent.archived !== true && (
          <ContextMenu.Item onClick={() => !isUpdatingStudent && handleSetArchived(true)}>
            Fahrschüler archivieren
          </ContextMenu.Item>
        )}
        {grants.has("editStudentPersonalData") && selectedStudent && selectedStudent.archived === true && (
          <ContextMenu.Item onClick={() => !isUpdatingStudent && handleSetArchived(false)}>
            Fahrschüler Archivstatus aufheben
          </ContextMenu.Item>
        )}
      </ContextMenu>
    </div>
  );
}

function _IconLabel({
  icon,
}: {
  icon: ReactElement;
  title: string; // The title prop is read by the custom header and used in the header tooltip
}): ReactElement {
  return <div>{icon}</div>;
}

function _KpiNumberField(props: {
  label: ReactElement;
  source: string;
  getValue: (student: Student) => number;
  options?: Intl.NumberFormatOptions;
  textAlign: "right";
}) {
  const { getValue, ...numberFieldProps } = props;
  const student = useRecordContext<Student>();
  if (getValue(student) === 0) {
    return <Box sx={{ color: "action.disabled" }}>–</Box>;
  }
  return <NumberField {...numberFieldProps} />;
}

function _StatusField(_props: { label: string; source: string }) {
  const student = useRecordContext<Student>();
  const { label: displayStatus, color: statusColor } = getStudentStatusDisplay(student.verboseStatus);
  const isOutlinedStatus = ["Inaktiv", "Abgebrochen"].includes(student.verboseStatus);
  return (
    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
      {student.verboseStatus === "Fertig" ? (
        <CheckCircleIcon sx={{ color: studentStatusColors.Aktiv, flexShrink: 0, fontSize: 13 }} />
      ) : (
        <TimelineDot
          sx={{
            ...dotStyle,
            width: 10,
            height: 10,
            flexShrink: 0,
            padding: 0,
            ...(isOutlinedStatus
              ? {
                  borderStyle: "solid",
                  borderWidth: 1.5,
                  borderColor: "text.disabled",
                  backgroundColor: "background.paper",
                }
              : { border: "none", backgroundColor: statusColor }),
          }}
        />
      )}
      <span>{displayStatus}</span>
    </Box>
  );
}

export function paymentStrategyDisplay(paymentStrategy: Student["paymentStrategy"]): {
  abbreviation: string;
  label: string;
} {
  switch (paymentStrategy) {
    case "upfrontPayment":
      return { abbreviation: "PP", label: "Prepaid / Vorauszahlung" };
    case "payAsYouDrive":
      return { abbreviation: "NT", label: "Nach jedem Termin" };
    case "purchaseOnAccount":
      return { abbreviation: "RE", label: "Rechnung" };
    case "externalBankTransfer":
      return { abbreviation: "BÜ", label: "Externe Banküberweisung" };
    default:
      return assertNever(paymentStrategy);
  }
}

function renderPaymentStrategy(student: Student) {
  const { abbreviation, label } = paymentStrategyDisplay(student.paymentStrategy);
  return (
    <Tooltip title={label}>
      <Chip label={abbreviation} size="small" aria-label={label} tabIndex={0} sx={paymentChipSx} />
    </Tooltip>
  );
}

function renderBookedTrainings(student: Student) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {student.bookedTrainings.map((training) => {
        const showBF17 = student.bf17 && training.isForDrivingLicense && isForCar(training);
        const label = `${training.shortLabel}${showBF17 ? " (BF17)" : ""}`;
        return <Chip key={training.id} label={label} size="small" sx={compactChipSx} />;
      })}
    </Box>
  );
}

export function renderAvatarAndName(student: Student) {
  return (
    <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
      <Avatar src={student.avatarUrl} sx={{ width: 32, height: 32 }} />
      <Column>
        <StudentLabels student={student} size="small" />
        <TextField source="name" />
      </Column>
    </Box>
  );
}
